// The paid/unpaid breakdown of an expense total, in the two shapes the screens need.
//
// One module because the same fact appears in four places — the retainer's expense
// tile, a billing job's expense tile, a topic's cost tile, and each row of the
// retainer's topics table. A total that means "all costs" on one screen and "costs
// we've actually paid" on another is how a figure stops being trusted, so all four
// read from here.
//
// QUIET WHEN THERE IS NOTHING TO SAY. Both render null once everything is paid. On a
// fully settled job "1,000 paid · 0 unpaid" would appear on every screen, every time,
// and a breakdown that is always present is one nobody reads — so it shows up only
// when money is actually still owed.
//
// DISPLAY ONLY. Neither of these touches a figure. The total above them is the accrual
// cost that profit is computed from — an expense counts from the moment it is
// committed — and splitting it here changes no profit, no balance, no settlement.
import he from '../i18n/he';
import Money from './Money';

// Guarded against float dust rather than `> 0`, matching the rounding tolerance the
// server's money comparisons use.
export const outstanding = (unpaid) => Number(unpaid) > 0.005;

// Sits under a StatTile and names both halves of the total above it.
export const ExpenseSplitNote = ({ paid, unpaid }) => {
  if (!outstanding(unpaid)) return null;
  return (
    <span className="muted small-note">
      {he.expenses.ofWhichPaid} <Money value={paid} />
      {' · '}
      {he.expenses.ofWhichUnpaid} <Money value={unpaid} />
    </span>
  );
};

// Rides along inside a table cell. A second column would be zero on most rows, which
// is noise on every one of them; a badge appears only where it applies.
export const UnpaidBadge = ({ unpaid }) => {
  if (!outstanding(unpaid)) return null;
  return (
    <span className="badge amber" style={{ marginInlineStart: 6 }}>
      {he.expenses.unpaid} <Money value={unpaid} />
    </span>
  );
};

// THE OTHER SLICE OF THE SAME TOTAL — the part of a cost that the CLIENT bears and that
// has not been charged on yet (migration 054).
//
// A different question from the paid/unpaid pair above, and the two are independent: that
// one asks whether OUR money has left yet, this one asks whether the cost is ours to bear
// at all. A cost can be paid and recoverable, or unpaid and absorbed.
//
// WHY IT EARNS A LINE. A retainer reading "costs 1,400" when 1,000 of that is money
// coming back reads as a loss dragging profit down, and nothing in the total says
// otherwise. Profit is gross on both sides — the cost counts, and the charge that
// recovers it counts as revenue on the invoice that raises it — so the figure only
// becomes legible once the screen names which part is returning.
//
// DISPLAY ONLY, like its neighbours. Nothing here touches profit, the gap or the monthly
// view; the total above it is the accrual cost profit is computed from, unchanged.
//
// QUIET WHEN THERE IS NOTHING TO SAY, by the same rule: once every recoverable cost has
// been charged on, the figure is zero and the line disappears rather than reporting "0
// to recover" on every screen forever.
// `absorbed` is optional. Where it is known — a JOB, which reads both halves from one
// statement — the note names the two sides of the split outright, because "how much of
// this job fell on us" is the question the job screen exists to answer. Where only the
// recoverable part is known (a retainer's rolled-up total), it names that alone rather
// than inferring the rest by subtraction: a cost recorded before the bearer question
// existed is in neither half, and calling it "ours" would be a guess.
export const ToRecoverNote = ({ toRecover, absorbed = null }) => {
  if (!outstanding(toRecover)) return null;
  return (
    <span className="muted small-note">
      {absorbed != null ? (
        <>
          {he.expenses.ofWhichSplit} <Money value={absorbed} /> {he.expenses.oursShort}
          {' · '}
          <Money value={toRecover} /> {he.expenses.toRecoverShort}
        </>
      ) : (
        <>{he.expenses.ofWhichToRecover} <Money value={toRecover} /></>
      )}
    </span>
  );
};

// THE RETAINER'S NET AFTER ONLY THE COSTS WE BEAR — `profit + toRecover`, and nothing
// more than that sum.
//
// WHY IT IS A LINE AND NOT THE PROFIT FIGURE. Profit stays gross on both sides, because
// the charge that recovers a client-borne cost becomes revenue on the invoice that
// raises it. Baking this into the stored figure would rewrite closed months the day
// someone raises a charge, and shift the settlement's `earned` line with it. As a line
// computed on screen from two figures already fetched, it can do neither.
//
// IT CANNOT DOUBLE-COUNT. `toRecover` counts only costs with no charge raised yet, so it
// falls to zero at the exact instant that cost's revenue appears — the two never coexist,
// and this line converges on plain profit the moment everything has been charged.
export const NetOfRecoveredNote = ({ profit, toRecover }) => {
  if (!outstanding(toRecover)) return null;
  return (
    <span className="muted small-note">
      {he.expenses.netAfterOurs} <strong><Money value={Number(profit) + Number(toRecover)} /></strong>
    </span>
  );
};

// WHY THIS JOB'S EXPENSE LIST SUMS HIGHER THAN ITS EXPENSE TILE.
//
// On a job that bills its own income beneath a retainer, the costs we ABSORB net against
// the retainer's profit rather than the job's — they are the retainer's own work, and
// only the brokerage cost and its charge belong to the billing side. The expense list
// below still shows every row filed on the job, because that is what was spent on it;
// the tile shows the part that nets against this job's revenue. Two honest figures that
// differ, so the screen says which is which rather than leaving a reader to find it.
export const OnRetainerNote = ({ onRetainer }) => {
  if (!outstanding(onRetainer)) return null;
  return (
    <span className="muted small-note">
      {he.expenses.onRetainerNote} <Money value={onRetainer} />
    </span>
  );
};

// The table-cell form of the note above, for the retainer's topics rows.
export const OnRetainerBadge = ({ onRetainer }) => {
  if (!outstanding(onRetainer)) return null;
  return (
    <span className="badge gray" style={{ marginInlineStart: 6 }}>
      {he.expenses.onRetainerShort} <Money value={onRetainer} />
    </span>
  );
};

// The table-cell form of the same figure, for the retainers list.
export const NetBadge = ({ profit, toRecover }) => {
  if (!outstanding(toRecover)) return null;
  return (
    <span className="badge blue" style={{ marginInlineStart: 6 }}>
      {he.expenses.netShort} <Money value={Number(profit) + Number(toRecover)} />
    </span>
  );
};

// BOTH SLICES UNDER ONE COST TOTAL — what every cost tile now shows.
//
// One component because three screens print the same pair (the retainer's expense tile,
// a billing job's, and a topic's cost tile) and a tile that splits a total one way on one
// screen and another way on the next is how a figure stops being trusted. Each half stays
// quiet on its own when it has nothing to say, so a fully paid and fully charged-on job
// shows the bare total exactly as it did before.
export const CostNotes = ({ paid, unpaid, toRecover, absorbed = null }) => (
  <>
    <ExpenseSplitNote paid={paid} unpaid={unpaid} />
    <ToRecoverNote toRecover={toRecover} absorbed={absorbed} />
  </>
);

// The table-cell form, beside UnpaidBadge on a topics row.
export const ToRecoverBadge = ({ toRecover }) => {
  if (!outstanding(toRecover)) return null;
  return (
    <span className="badge blue" style={{ marginInlineStart: 6 }}>
      {he.expenses.toRecoverShort} <Money value={toRecover} />
    </span>
  );
};
